import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Card,
  TextField,
  MenuItem,
  Typography,
  Grid,
  InputAdornment,
  Paper,
  Chip,
  IconButton,
  Avatar,
  Alert,
  Tabs,
  Tab,
  FormControlLabel,
  Switch,
  Autocomplete,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Stack,
  alpha,
  useTheme,
  Tooltip,
} from '@mui/material'
import {
  ArrowBack,
  Save,
  Preview,
  CloudUpload,
  Category,
  LocalOffer,
  Inventory2 as Package,
  Link as LinkIcon,
  Edit,
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Palette as ColorIcon,
  AutoAwesome as AutoAwesomeIcon,
  Bolt as BoltIcon,
  TrendingUp,
} from '@mui/icons-material'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { ProductStatus, GSTSlab, Variant } from '../types/product'
import { 
  useGetProductQuery, 
  useCreateProductMutation, 
  useUpdateProductMutation,
  useGenerateDescriptionMutation,
} from '../store/services/productApi'
import { useGetCategoriesQuery } from '../store/services/categoryApi'
import { productService } from '../services/productService'
import ImageUpload from '../components/ImageUpload'
import MultiImageUpload from '../components/MultiImageUpload'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`product-tabpanel-${index}`}
      aria-labelledby={`product-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  )
}

const INDIC_LANGUAGES = [
  'English',
  'Hindi',
  'Gujarati',
  'Tamil',
  'Telugu',
  'Marathi',
  'Bengali',
  'Kannada',
  'Punjabi',
  'Odia',
  'Malayalam',
]

const AI_TONES = [
  'Professional B2B',
  'Marketing Highlight',
  'Technical Specs',
  'Wholesale & Logistics Focus',
]

const ProductAddEdit: React.FC = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { id } = useParams()
  const location = useLocation()
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo || '/admin/products'
  const { enqueueSnackbar } = useSnackbar()
  const [activeTab, setActiveTab] = useState(0)

  // Sarvam AI Generator State
  const [sarvamDialogOpen, setSarvamDialogOpen] = useState(false)
  const [aiLanguage, setAiLanguage] = useState('English')
  const [aiTone, setAiTone] = useState('Professional B2B')
  const [aiKeyFeatures, setAiKeyFeatures] = useState('')
  const [generatedAiDesc, setGeneratedAiDesc] = useState('')

  const [generateDescription, { isLoading: isGeneratingAi }] = useGenerateDescriptionMutation()

  // Variant management state
  const [variants, setVariants] = useState<Variant[]>([])
  const [showVariantDialog, setShowVariantDialog] = useState(false)
  const [editingVariant, setEditingVariant] = useState<(Variant & { index: number }) | null>(null)
  const [variantFormData, setVariantFormData] = useState<{
    sku: string
    attributes: Record<string, any>
    mrp: number
    sale_price: number
    wholesale_price: number
    purchase_price: number
    weight: number
    dimensions: { length: number; width: number; height: number }
    is_active: boolean
  }>({
    sku: '',
    attributes: {},
    mrp: 0,
    sale_price: 0,
    wholesale_price: 0,
    purchase_price: 0,
    weight: 0,
    dimensions: { length: 0, width: 0, height: 0 },
    is_active: true,
  })

  const isEdit = Boolean(id)

  const { data: product, isLoading: isLoadingProduct, error: loadError } = useGetProductQuery(id!, {
    skip: !isEdit,
  })

  const { data: categoriesData, isLoading: isLoadingCategories } = useGetCategoriesQuery({
    per_page: 100,
  })

  const [createProduct, { isLoading: isCreating }] = useCreateProductMutation()
  const [updateProduct, { isLoading: isUpdating }] = useUpdateProductMutation()

  const isLoading = isCreating || isUpdating
  const categories = categoriesData?.items || []

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    item_code: '',
    sku: '',
    hsn_code: '',
    description: '',
    brand_id: '',
    brand_name: '',

    category_id: '',
    status: ProductStatus.DRAFT,
    is_active: true,
    is_online_enabled: true,
    is_clearance: false,
    is_deal: false,

    inner_pack_size: 1,
    carton_size: 1,
    carton_rate: 0,
    moq: 1,
    moa: 0,
    set_size: 1,
    is_set_selling: false,

    weight: 0,
    length: 0,
    width: 0,
    height: 0,

    mrp: 0,
    sale_price: 0,
    wholesale_price: 0,
    purchase_price: 0,
    discount_percentage: 0,
    gst_rate: 18,
    gst_slab: GSTSlab.EIGHTEEN,

    amazon_link: '',
    flipkart_link: '',
    meesho_link: '',

    main_image: '',
    additional_images: [] as string[],
  })

  useEffect(() => {
    if (product && isEdit) {
      setFormData({
        name: product.name || '',
        item_code: product.item_code || '',
        sku: product.sku || '',
        hsn_code: product.hsn_code || '',
        description: product.description || '',
        brand_id: product.brand_id || '',
        brand_name: product.brand?.name || '',

        category_id: product.category_id || '',
        status: product.status || ProductStatus.DRAFT,
        is_active: product.is_active ?? true,
        is_online_enabled: product.is_online_enabled ?? true,
        is_clearance: product.is_clearance ?? false,
        is_deal: product.is_deal ?? false,

        inner_pack_size: product.inner_box_quantity || 1,
        carton_size: product.carton_quantity || 1,
        carton_rate: Number(product.carton_rate) || 0,
        moq: product.moq || 1,
        moa: 0,
        set_size: product.set_size || 1,
        is_set_selling: product.is_set_selling || false,

        weight: Number(product.weight) || 0,
        length: product.dimensions?.length || 0,
        width: product.dimensions?.width || 0,
        height: product.dimensions?.height || 0,

        mrp: Number(product.mrp) || 0,
        sale_price: Number(product.sale_price) || 0,
        wholesale_price: Number(product.wholesale_price) || 0,
        purchase_price: Number(product.purchase_price) || 0,
        discount_percentage: 0,
        gst_rate: Number(product.gst_rate) || 18,
        gst_slab: product.gst_slab || GSTSlab.EIGHTEEN,

        amazon_link: (product.product_metadata?.marketplace_links as any)?.amazon || '',
        flipkart_link: (product.product_metadata?.marketplace_links as any)?.flipkart || '',
        meesho_link: (product.product_metadata?.marketplace_links as any)?.meesho || '',

        main_image: product.image || '',
        additional_images: product.images || [],
      })

      if (product.variants && product.variants.length > 0) {
        setVariants(product.variants)
      }
    }
  }, [product, isEdit])

  useEffect(() => {
    if (loadError) {
      const err = loadError as any
      let errorMessage = 'Failed to load product data'
      if (err?.status === 404) errorMessage = 'Product not found'
      else if (err?.status === 401) errorMessage = 'Please log in to view product'
      else if (err?.data?.detail) errorMessage = err.data.detail
      enqueueSnackbar(errorMessage, { variant: 'error' })
    }
  }, [loadError, enqueueSnackbar])

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value }
      if (field === 'sale_price' || field === 'carton_size') {
        const ctnSize = field === 'carton_size' ? Number(value) : prev.carton_size
        const unitSale = field === 'sale_price' ? Number(value) : prev.sale_price
        if (ctnSize > 0 && unitSale > 0) {
          updated.carton_rate = unitSale * ctnSize
        }
      }
      return updated
    })
  }

  const calculateGrossMargin = (salePrice: number, purchasePrice: number): number => {
    if (salePrice <= 0 || purchasePrice < 0) return 0
    return ((salePrice - purchasePrice) / salePrice) * 100
  }

  const grossMargin = React.useMemo(() => {
    return calculateGrossMargin(formData.sale_price, formData.purchase_price)
  }, [formData.sale_price, formData.purchase_price])

  const wholesaleMargin = React.useMemo(() => {
    return calculateGrossMargin(formData.wholesale_price, formData.purchase_price)
  }, [formData.wholesale_price, formData.purchase_price])

  const handleAutoGenerateSku = () => {
    if (!formData.name.trim()) {
      enqueueSnackbar('Please enter a product name first', { variant: 'warning' })
      return
    }
    const cleanName = formData.name.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 6)
    const randomNum = Math.floor(1000 + Math.random() * 9000)
    const generatedCode = `${cleanName}-${randomNum}`
    setFormData(prev => ({
      ...prev,
      item_code: prev.item_code || generatedCode,
      sku: prev.sku || `${generatedCode}-SKU`
    }))
    enqueueSnackbar(`Auto-generated Item Code & SKU`, { variant: 'info' })
  }

  const handleGenerateAiDescription = async () => {
    if (!formData.name.trim()) {
      enqueueSnackbar('Please enter a Product Name first', { variant: 'warning' })
      return
    }
    try {
      const selectedCat = categories.find(c => c.id === formData.category_id)?.name
      const res = await generateDescription({
        name: formData.name,
        category_name: selectedCat || '',
        brand_name: formData.brand_name || '',
        hsn_code: formData.hsn_code || '',
        key_features: aiKeyFeatures || undefined,
        language: aiLanguage,
        tone: aiTone,
      }).unwrap()

      if (res?.description) {
        setGeneratedAiDesc(res.description)
        enqueueSnackbar('Generated AI Description using Sarvam AI!', { variant: 'success' })
      }
    } catch (err: any) {
      enqueueSnackbar(err?.data?.detail || 'Failed to generate AI description', { variant: 'error' })
    }
  }

  const handleApplyAiDescription = () => {
    if (generatedAiDesc) {
      setFormData(prev => ({ ...prev, description: generatedAiDesc }))
      setSarvamDialogOpen(false)
      enqueueSnackbar('Applied AI description to product!', { variant: 'success' })
    }
  }

  const handleSave = async () => {
    try {
      const pricingErrors: string[] = []
      if (formData.sale_price > formData.mrp) pricingErrors.push('Sale price cannot exceed MRP')
      if (formData.wholesale_price > formData.sale_price) pricingErrors.push('Wholesale price cannot exceed sale price')
      if (formData.purchase_price > formData.wholesale_price) pricingErrors.push('Purchase price cannot exceed wholesale price')
      if (formData.purchase_price < 0) pricingErrors.push('Purchase price must be positive')

      if (pricingErrors.length > 0) {
        enqueueSnackbar(pricingErrors.join('. '), { variant: 'error' })
        return
      }

      const marketplaceLinks: any = {}
      if (formData.amazon_link) marketplaceLinks.amazon = formData.amazon_link
      if (formData.flipkart_link) marketplaceLinks.flipkart = formData.flipkart_link
      if (formData.meesho_link) marketplaceLinks.meesho = formData.meesho_link

      const productData: any = {
        name: formData.name,
        sku: formData.sku || `${formData.name.slice(0, 4).toUpperCase()}-${Math.floor(1000+Math.random()*9000)}`,
        item_code: formData.item_code || `${formData.name.slice(0, 4).toUpperCase()}-${Math.floor(1000+Math.random()*9000)}`,
        hsn_code: formData.hsn_code,
        description: formData.description,
        category_id: formData.category_id,

        mrp: formData.mrp,
        sale_price: formData.sale_price,
        wholesale_price: formData.wholesale_price,
        purchase_price: formData.purchase_price,
        gst_rate: formData.gst_rate,
        gst_slab: formData.gst_slab,

        moq: formData.moq || 1,
        inner_box_quantity: formData.inner_pack_size || null,
        carton_quantity: formData.carton_size || null,
        carton_rate: formData.carton_size ? formData.carton_rate : null,
        set_size: formData.set_size || 1,
        is_set_selling: formData.is_set_selling || false,

        status: formData.status,
        is_active: formData.is_active,
        is_online_enabled: formData.is_online_enabled,
        is_clearance: formData.is_clearance,
        is_deal: formData.is_deal,

        weight: formData.weight || 0,
        dimensions: {
          length: formData.length || 0,
          width: formData.width || 0,
          height: formData.height || 0,
        },
      }

      if (formData.brand_id) productData.brand_id = formData.brand_id
      if (formData.main_image) productData.image = formData.main_image
      if (formData.additional_images && formData.additional_images.length > 0) {
        productData.images = formData.additional_images.filter(img => img && img.trim() !== '')
      }
      if (Object.keys(marketplaceLinks).length > 0) {
        productData.marketplace_links = marketplaceLinks
      }
      if (variants.length > 0) {
        productData.variants = variants.map(variant => ({
          name: variant.name,
          item_code: variant.item_code || variant.sku,
          sku: variant.sku,
          attributes: variant.attributes || {},
          mrp: variant.mrp || null,
          sale_price: variant.sale_price || null,
          wholesale_price: variant.wholesale_price || null,
          purchase_price: variant.purchase_price || null,
          weight: variant.weight || null,
          dimensions: variant.dimensions || {},
          is_active: variant.is_active ?? true,
          is_online_enabled: variant.is_online_enabled ?? true,
        }))
      }

      if (isEdit && id) {
        await updateProduct({ id, data: productData }).unwrap()
        enqueueSnackbar('Product updated successfully!', { variant: 'success' })
      } else {
        await createProduct(productData).unwrap()
        enqueueSnackbar('Product created successfully!', { variant: 'success' })
      }

      navigate(returnTo)
    } catch (error: any) {
      console.error('Save error:', error)
      enqueueSnackbar(error?.data?.detail || 'Failed to save product', { variant: 'error' })
    }
  }

  const handlePreview = () => {
    if (isEdit && id) {
      const baseUrl = import.meta.env.VITE_CUSTOMER_PORTAL_URL || 'http://localhost:3001'
      window.open(`${baseUrl}/products/${id}`, '_blank')
    } else {
      enqueueSnackbar('Please save the product first to enable preview', { variant: 'info' })
    }
  }

  // Variant handlers
  const handleAddVariant = () => {
    setEditingVariant(null)
    setVariantFormData({
      sku: `${formData.sku || 'PROD'}-V${variants.length + 1}`,
      attributes: {},
      mrp: 0,
      sale_price: 0,
      wholesale_price: 0,
      purchase_price: 0,
      weight: 0,
      dimensions: { length: 0, width: 0, height: 0 },
      is_active: true,
    })
    setShowVariantDialog(true)
  }

  const handleEditVariant = (variant: Variant, index: number) => {
    setEditingVariant({ ...variant, index })
    setVariantFormData({
      sku: variant.sku || '',
      attributes: variant.attributes || {},
      mrp: variant.mrp || 0,
      sale_price: variant.sale_price || 0,
      wholesale_price: variant.wholesale_price || 0,
      purchase_price: variant.purchase_price || 0,
      weight: variant.weight || 0,
      dimensions: { 
        length: variant.dimensions?.length || 0, 
        width: variant.dimensions?.width || 0, 
        height: variant.dimensions?.height || 0 
      },
      is_active: variant.is_active ?? true,
    })
    setShowVariantDialog(true)
  }

  const handleSaveVariant = () => {
    if (!variantFormData.sku.trim()) {
      enqueueSnackbar('SKU is required for variant', { variant: 'error' })
      return
    }

    const isDuplicate = variants.some((v, index) => 
      v.sku === variantFormData.sku && 
      (!editingVariant || editingVariant.index !== index)
    )
    
    if (isDuplicate) {
      enqueueSnackbar('SKU already exists.', { variant: 'error' })
      return
    }

    const attributeString = Object.entries(variantFormData.attributes || {})
      .map(([key, value]) => `${key}: ${value}`)
      .join(', ')
    
    const variantName = attributeString 
      ? `${formData.name} (${attributeString})`
      : `${formData.name} - Variant ${variants.length + 1}`

    const newVariant: any = {
      ...variantFormData,
      name: variantName,
      item_code: variantFormData.sku,
      mrp: variantFormData.mrp || null,
      sale_price: variantFormData.sale_price || null,
      wholesale_price: variantFormData.wholesale_price || null,
      purchase_price: variantFormData.purchase_price || null,
      weight: variantFormData.weight || null,
      dimensions: (variantFormData.dimensions.length || variantFormData.dimensions.width || variantFormData.dimensions.height)
        ? variantFormData.dimensions
        : {},
    }

    if (editingVariant) {
      const updatedVariants = [...variants]
      updatedVariants[editingVariant.index] = newVariant
      setVariants(updatedVariants)
      enqueueSnackbar('Variant updated', { variant: 'success' })
    } else {
      setVariants([...variants, newVariant])
      enqueueSnackbar('Variant added', { variant: 'success' })
    }

    setShowVariantDialog(false)
    setEditingVariant(null)
  }

  const handleDeleteVariant = (index: number) => {
    setVariants(prev => prev.filter((_, i) => i !== index))
    enqueueSnackbar('Variant deleted', { variant: 'success' })
  }

  const handleDuplicateVariant = (variant: Variant) => {
    setVariants(prev => [...prev, { ...variant, sku: `${variant.sku}-COPY` }])
    enqueueSnackbar('Variant duplicated', { variant: 'success' })
  }

  const handleVariantFormChange = (field: string, value: any) => {
    if (field.startsWith('attributes.')) {
      const attrKey = field.replace('attributes.', '')
      setVariantFormData(prev => ({
        ...prev,
        attributes: { ...prev.attributes, [attrKey]: value }
      }))
    } else if (field.startsWith('dimensions.')) {
      const dimKey = field.replace('dimensions.', '')
      setVariantFormData(prev => ({
        ...prev,
        dimensions: { ...prev.dimensions, [dimKey]: value }
      }))
    } else {
      setVariantFormData(prev => ({ ...prev, [field]: value }))
    }
  }

  const addAttributeField = () => {
    const attrName = prompt('Enter attribute name (e.g., Color, Size, Material):')
    if (attrName && attrName.trim()) {
      setVariantFormData(prev => ({
        ...prev,
        attributes: { ...prev.attributes, [attrName.trim()]: '' }
      }))
    }
  }

  const removeAttributeField = (attrKey: string) => {
    setVariantFormData(prev => {
      const newAttributes = { ...prev.attributes }
      delete newAttributes[attrKey]
      return { ...prev, attributes: newAttributes }
    })
  }

  if (isEdit && isLoadingProduct) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#F8FAFC' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={48} />
          <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>Loading product data...</Typography>
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC' }}>
      {/* Sticky Action Header */}
      <Paper 
        elevation={1}
        sx={{ 
          position: 'sticky',
          top: 0,
          zIndex: 1100,
          background: 'linear-gradient(135deg, #1e88e5 0%, #0d47a1 100%)',
          color: 'white',
          px: { xs: 2, sm: 3 },
          py: 1.5,
          borderRadius: 0,
          boxShadow: '0 2px 10px rgba(0,0,0,0.15)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <IconButton onClick={() => navigate(returnTo)} sx={{ color: 'white' }} size="small">
              <ArrowBack />
            </IconButton>
            <Box>
              <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }}>
                {isEdit ? `Edit Product: ${formData.name || 'Untitled'}` : 'Add New Product'}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85, display: 'block' }}>
                {isEdit ? 'Update product information and B2B specs' : 'Fill details to add to catalog'}
              </Typography>
            </Box>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Preview fontSize="small" />}
              onClick={handlePreview}
              sx={{ borderColor: 'rgba(255,255,255,0.6)', color: 'white', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.15)' } }}
            >
              Preview
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<Save fontSize="small" />}
              onClick={handleSave}
              disabled={isLoading}
              sx={{ bgcolor: '#f44336', '&:hover': { bgcolor: '#d32f2f' }, fontWeight: 700 }}
            >
              {isLoading ? 'Saving...' : (isEdit ? 'Update Product' : 'Save Product')}
            </Button>
          </Stack>
        </Box>
      </Paper>

      {/* Main Content Area */}
      <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
        <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          {/* Navigation Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
            <Tabs 
              value={activeTab} 
              onChange={(_, newValue) => setActiveTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: 48,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  minHeight: 48,
                  py: 1,
                }
              }}
            >
              <Tab label="Basic Info" icon={<Edit fontSize="small" />} iconPosition="start" />
              <Tab label={`Variants (${variants.length})`} icon={<ColorIcon fontSize="small" />} iconPosition="start" />
              <Tab label="Images" icon={<CloudUpload fontSize="small" />} iconPosition="start" />
              <Tab label="B2B Packaging" icon={<Package fontSize="small" />} iconPosition="start" />
              <Tab label="Pricing & GST" icon={<LocalOffer fontSize="small" />} iconPosition="start" />
              <Tab label="Marketplace" icon={<LinkIcon fontSize="small" />} iconPosition="start" />
            </Tabs>
          </Box>

          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            {/* Basic Information Tab */}
            <TabPanel value={activeTab} index={0}>
              <Grid container spacing={2.5}>
                <Grid item xs={12}>
                  <Card elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                      <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36 }}>
                        <Category fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={700}>Product Identity & Codes</Typography>
                        <Typography variant="caption" color="text.secondary">Name, SKU, Item Code, and classification</Typography>
                      </Box>
                    </Box>

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Product Name"
                          value={formData.name}
                          onChange={(e) => handleInputChange('name', e.target.value)}
                          required
                          size="small"
                          helperText="Full product title displayed to customers"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Brand Name"
                          value={formData.brand_name}
                          onChange={(e) => handleInputChange('brand_name', e.target.value)}
                          placeholder="e.g. Dhanshika, Acme, Vendor Brand"
                          helperText="Manufacturer or Brand Name"
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Item Code"
                          value={formData.item_code}
                          onChange={(e) => handleInputChange('item_code', e.target.value)}
                          placeholder="e.g. ITM-1002"
                          helperText="Unique Item Code or Vendor Part No."
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="SKU Code"
                          value={formData.sku}
                          onChange={(e) => handleInputChange('sku', e.target.value)}
                          placeholder="e.g. SKU-1002"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Tooltip title="Auto-generate SKU from name">
                                  <IconButton size="small" onClick={handleAutoGenerateSku}>
                                    <BoltIcon fontSize="small" color="primary" />
                                  </IconButton>
                                </Tooltip>
                              </InputAdornment>
                            ),
                          }}
                          helperText="Stock Keeping Unit (SKU)"
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="HSN Code"
                          value={formData.hsn_code}
                          onChange={(e) => handleInputChange('hsn_code', e.target.value)}
                          required
                          placeholder="e.g. 6204"
                          helperText="HSN Code for GST Invoice"
                        />
                      </Grid>

                      {/* Category & Status */}
                      <Grid item xs={12} md={6}>
                        <Autocomplete
                          options={categories}
                          getOptionLabel={(option) => option.name}
                          value={categories.find(cat => cat.id === formData.category_id) || null}
                          onChange={(_, value) => handleInputChange('category_id', value?.id || '')}
                          loading={isLoadingCategories}
                          disabled={isLoadingCategories}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Category"
                              required
                              size="small"
                              helperText="Select primary product category"
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          label="Product Status"
                          value={formData.status}
                          onChange={(e) => handleInputChange('status', e.target.value)}
                        >
                          <MenuItem value={ProductStatus.DRAFT}>Draft</MenuItem>
                          <MenuItem value={ProductStatus.ACTIVE}>Active</MenuItem>
                          <MenuItem value={ProductStatus.OUT_OF_STOCK}>Out of Stock</MenuItem>
                          <MenuItem value={ProductStatus.ARCHIVED}>Archived</MenuItem>
                          <MenuItem value={ProductStatus.DISCONTINUED}>Discontinued</MenuItem>
                        </TextField>
                      </Grid>

                      {/* Status Toggles Section */}
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: 'block', mb: 1 }}>
                          Promotional Flags & Store Visibility:
                        </Typography>
                        <Stack direction="row" spacing={2} flexWrap="wrap">
                          <FormControlLabel
                            control={<Switch checked={formData.is_active} onChange={(e) => handleInputChange('is_active', e.target.checked)} size="small" color="primary" />}
                            label={<Typography variant="body2">Is Active</Typography>}
                          />
                          <FormControlLabel
                            control={<Switch checked={formData.is_online_enabled} onChange={(e) => handleInputChange('is_online_enabled', e.target.checked)} size="small" color="primary" />}
                            label={<Typography variant="body2">Online Store</Typography>}
                          />
                          <FormControlLabel
                            control={<Switch checked={formData.is_clearance} onChange={(e) => handleInputChange('is_clearance', e.target.checked)} size="small" color="warning" />}
                            label={<Chip label="Clearance" size="small" color="warning" variant="outlined" />}
                          />
                          <FormControlLabel
                            control={<Switch checked={formData.is_deal} onChange={(e) => handleInputChange('is_deal', e.target.checked)} size="small" color="error" />}
                            label={<Chip label="Hot Deal" size="small" color="error" variant="outlined" />}
                          />
                        </Stack>
                      </Grid>

                      {/* Description with Sarvam AI Generator Button */}
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" fontWeight={600}>Product Description</Typography>
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<AutoAwesomeIcon fontSize="small" />}
                            onClick={() => setSarvamDialogOpen(true)}
                            sx={{
                              background: 'linear-gradient(135deg, #7c4dff 0%, #651fff 100%)',
                              color: 'white',
                              fontWeight: 700,
                              textTransform: 'none',
                              fontSize: '0.78rem',
                              borderRadius: 1.5,
                              px: 1.5,
                              '&:hover': { background: 'linear-gradient(135deg, #651fff 0%, #4527a0 100%)' }
                            }}
                          >
                            Generate with Sarvam AI ✨
                          </Button>
                        </Box>
                        <TextField
                          fullWidth
                          value={formData.description}
                          onChange={(e) => handleInputChange('description', e.target.value)}
                          multiline
                          rows={5}
                          variant="outlined"
                          placeholder="Enter or generate complete product specifications, materials, and features..."
                        />
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>
              </Grid>
            </TabPanel>

            {/* Variants Tab */}
            <TabPanel value={activeTab} index={1}>
              <Card elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ bgcolor: 'secondary.main', width: 36, height: 36 }}>
                      <ColorIcon fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={700}>Product Variants</Typography>
                      <Typography variant="caption" color="text.secondary">Configure sizes, colors, or SKU options</Typography>
                    </Box>
                  </Box>
                  <Button variant="contained" color="secondary" size="small" startIcon={<AddIcon />} onClick={handleAddVariant} sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600 }}>
                    Add Variant
                  </Button>
                </Box>

                {variants.length === 0 ? (
                  <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2, bgcolor: '#F8FAFC' }}>
                    <Typography color="text.secondary" variant="body2">No variants created for this product yet.</Typography>
                  </Paper>
                ) : (
                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                    <Table size="small">
                      <TableHead sx={{ bgcolor: 'grey.100' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>SKU</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Variant Name</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Attributes</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Price (₹)</TableCell>
                          <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {variants.map((variant, index) => (
                          <TableRow key={index}>
                            <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{variant.sku}</Typography></TableCell>
                            <TableCell><Typography variant="body2" fontWeight={600}>{variant.name}</Typography></TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                {Object.entries(variant.attributes || {}).map(([k, v]) => (
                                  <Chip key={k} label={`${k}: ${v}`} size="small" variant="outlined" sx={{ fontSize: '0.68rem', height: 18 }} />
                                ))}
                              </Stack>
                            </TableCell>
                            <TableCell><Typography variant="body2" color="success.main" fontWeight={700}>₹{variant.sale_price || formData.sale_price}</Typography></TableCell>
                            <TableCell align="right">
                              <IconButton size="small" color="primary" onClick={() => handleEditVariant(variant, index)}><Edit fontSize="small" /></IconButton>
                              <IconButton size="small" color="info" onClick={() => handleDuplicateVariant(variant)}><CopyIcon fontSize="small" /></IconButton>
                              <IconButton size="small" color="error" onClick={() => handleDeleteVariant(index)}><DeleteIcon fontSize="small" /></IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Card>
            </TabPanel>

            {/* Images Tab */}
            <TabPanel value={activeTab} index={2}>
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={6}>
                  <Card elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>Main Product Cover Image</Typography>
                    <ImageUpload
                      value={formData.main_image}
                      onChange={(url) => handleInputChange('main_image', url)}
                    />
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>Additional Gallery Images</Typography>
                    <MultiImageUpload
                      values={formData.additional_images}
                      onChange={(urls) => handleInputChange('additional_images', urls)}
                    />
                  </Card>
                </Grid>
              </Grid>
            </TabPanel>

            {/* B2B Packaging Specifications Tab */}
            <TabPanel value={activeTab} index={3}>
              <Card elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'success.main', width: 36, height: 36 }}>
                    <Package fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700}>B2B Wholesale Packaging & MOQ</Typography>
                    <Typography variant="caption" color="text.secondary">Set sizes, inner box count, and carton packaging specs</Typography>
                  </Box>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ p: 2, bgcolor: alpha(theme.palette.success.main, 0.03) }}>
                      <FormControlLabel
                        control={<Switch checked={formData.is_set_selling} onChange={(e) => handleInputChange('is_set_selling', e.target.checked)} color="success" />}
                        label={<Typography variant="body2" fontWeight={700}>Enable Set Selling (Multiples)</Typography>}
                      />
                      {formData.is_set_selling && (
                        <TextField
                          fullWidth
                          size="small"
                          label="Set Size (Units per Set)"
                          type="number"
                          value={formData.set_size}
                          onChange={(e) => handleInputChange('set_size', Number(e.target.value))}
                          sx={{ mt: 1.5 }}
                        />
                      )}
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Grid container spacing={1.5}>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Inner Pack Size (Pcs)"
                          type="number"
                          value={formData.inner_pack_size}
                          onChange={(e) => handleInputChange('inner_pack_size', Number(e.target.value))}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Carton Quantity (Pcs)"
                          type="number"
                          value={formData.carton_size}
                          onChange={(e) => handleInputChange('carton_size', Number(e.target.value))}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="MOQ (Min Order Qty)"
                          type="number"
                          value={formData.moq}
                          onChange={(e) => handleInputChange('moq', Number(e.target.value))}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Carton Rate (₹)"
                          type="number"
                          value={formData.carton_rate}
                          onChange={(e) => handleInputChange('carton_rate', Number(e.target.value))}
                          helperText="Auto-calculated from Unit Price × Carton Qty"
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Card>
            </TabPanel>

            {/* Pricing & Profitability Tab */}
            <TabPanel value={activeTab} index={4}>
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={8}>
                  <Card elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                      <Avatar sx={{ bgcolor: 'warning.main', width: 36, height: 36 }}>
                        <LocalOffer fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={700}>B2B Tiered Pricing & GST</Typography>
                        <Typography variant="caption" color="text.secondary">MRP, Retail Sale Price, Wholesale Rate, and Purchase Cost</Typography>
                      </Box>
                    </Box>

                    <Grid container spacing={2}>
                      <Grid item xs={6} md={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label="MRP (₹)"
                          type="number"
                          value={formData.mrp}
                          onChange={(e) => handleInputChange('mrp', Number(e.target.value))}
                        />
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Sale Price (₹)"
                          type="number"
                          value={formData.sale_price}
                          onChange={(e) => handleInputChange('sale_price', Number(e.target.value))}
                        />
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Wholesale Price (₹)"
                          type="number"
                          value={formData.wholesale_price}
                          onChange={(e) => handleInputChange('wholesale_price', Number(e.target.value))}
                        />
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Purchase Price (₹)"
                          type="number"
                          value={formData.purchase_price}
                          onChange={(e) => handleInputChange('purchase_price', Number(e.target.value))}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="GST Rate (%)"
                          type="number"
                          value={formData.gst_rate}
                          onChange={(e) => handleInputChange('gst_rate', Number(e.target.value))}
                        />
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>

                {/* Profitability Calculator Card */}
                <Grid item xs={12} md={4}>
                  <Card elevation={0} sx={{ p: 2.5, border: '1.5px solid', borderColor: 'primary.light', borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
                    <Typography variant="subtitle2" fontWeight={700} color="primary.main" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TrendingUp fontSize="small" /> B2B Profitability Calculator
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                      <Typography variant="body2" color="text.secondary">Sale Margin:</Typography>
                      <Typography variant="body2" fontWeight={700} color={grossMargin >= 20 ? 'success.main' : 'warning.main'}>
                        {grossMargin.toFixed(1)}%
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                      <Typography variant="body2" color="text.secondary">Wholesale Margin:</Typography>
                      <Typography variant="body2" fontWeight={700} color={wholesaleMargin >= 10 ? 'success.main' : 'warning.main'}>
                        {wholesaleMargin.toFixed(1)}%
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
                      <Typography variant="body2" fontWeight={600}>Profit per unit:</Typography>
                      <Typography variant="body2" fontWeight={700} color="success.main">
                        ₹{(formData.sale_price - formData.purchase_price).toFixed(2)}
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
              </Grid>
            </TabPanel>

            {/* Marketplace Tab */}
            <TabPanel value={activeTab} index={5}>
              <Card elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>Omnichannel Marketplace Cross-Links</Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth size="small" label="Amazon URL" value={formData.amazon_link} onChange={(e) => handleInputChange('amazon_link', e.target.value)} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth size="small" label="Flipkart URL" value={formData.flipkart_link} onChange={(e) => handleInputChange('flipkart_link', e.target.value)} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth size="small" label="Meesho URL" value={formData.meesho_link} onChange={(e) => handleInputChange('meesho_link', e.target.value)} />
                  </Grid>
                </Grid>
              </Card>
            </TabPanel>
          </Box>
        </Card>
      </Box>

      {/* Sarvam AI Description Generator Dialog */}
      <Dialog open={sarvamDialogOpen} onClose={() => setSarvamDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesomeIcon sx={{ color: '#651fff' }} /> Sarvam AI B2B Description Generator
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="info" sx={{ fontSize: '0.82rem' }}>
              Sarvam AI (Model: <code>sarvam-105b</code>) generates multilingual B2B descriptions across 10+ Indic languages.
            </Alert>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Target Language"
                  value={aiLanguage}
                  onChange={(e) => setAiLanguage(e.target.value)}
                >
                  {INDIC_LANGUAGES.map((lang) => (
                    <MenuItem key={lang} value={lang}>{lang}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Description Tone"
                  value={aiTone}
                  onChange={(e) => setAiTone(e.target.value)}
                >
                  {AI_TONES.map((tone) => (
                    <MenuItem key={tone} value={tone}>{tone}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            <TextField
              fullWidth
              size="small"
              label="Key Features / Highlights (Optional Prompt)"
              value={aiKeyFeatures}
              onChange={(e) => setAiKeyFeatures(e.target.value)}
              placeholder="e.g. Handwoven pure silk, 180 GSM, water-resistant packaging, 5-year warranty..."
              multiline
              rows={2}
            />

            {generatedAiDesc && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary">Generated Output Preview:</Typography>
                <Paper variant="outlined" sx={{ p: 2, mt: 0.5, bgcolor: '#F8FAFC', maxHeight: 200, overflowY: 'auto' }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.85rem', fontFamily: 'monospace' }}>
                    {generatedAiDesc}
                  </Typography>
                </Paper>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setSarvamDialogOpen(false)} color="inherit">Cancel</Button>
          <Button
            onClick={handleGenerateAiDescription}
            disabled={isGeneratingAi}
            variant="outlined"
            startIcon={isGeneratingAi ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
            sx={{ color: '#651fff', borderColor: '#651fff' }}
          >
            {isGeneratingAi ? 'Generating...' : 'Generate AI Description'}
          </Button>
          {generatedAiDesc && (
            <Button onClick={handleApplyAiDescription} variant="contained" sx={{ bgcolor: '#651fff', '&:hover': { bgcolor: '#4527a0' } }}>
              Apply to Product
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ProductAddEdit